import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import axios from "axios";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {Header} from "./DataSourceDocumentList.styled";
import {Button, Tooltip} from "@mui/material";
import {ReactComponent as PlusIcon} from "../../assets/icons/users-plus.svg";
import BackButton from "../../components/common/BackButton";
import {ReactComponent as ChatIcon} from "../../assets/icons/chat.svg";
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import DeleteIcon from '@mui/icons-material/Delete';
import AddDocumentDialog from "../../components/data-sources/AddDocumentDialog";
import {DataSource, DocumentInDataSource} from "../../models/models";
import {useApi} from "../../hooks/UseApi";
import {useAuth} from "../../core/AuthContext";
import {usePageTitle} from '../../hooks/usePageTitle';
import {useAppConstants} from '../../config/appConstants';
import ConfirmationDialog from "../../components/dialogs/ConfirmationDialog";
import {useTranslation} from 'react-i18next';
import ResponsiveTable, {ResponsiveTableColumn} from "../../components/common/ResponsiveTable";
import {
    ActionButton,
    ContextMenu,
    ContextMenuItem,
    OptionsButton
} from "../../components/data-sources/DataSourceTable.styled";
import {ReactComponent as MoreIcon} from "../../assets/icons/more.svg";
import {StyledTableContainer} from "../../components/common/TableContainer.styled";

const DataSourceDocumentList: React.FC = () => {
    const {t} = useTranslation();
    const {DATA_SOURCE_TITLE_PREFIX} = useAppConstants();
    const {id} = useParams<{ id: string }>();
    const [documentsDataSourceId, setDocumentsDataSourceId] = useState("");
    const [documents, setDocuments] = useState<DocumentInDataSource[]>([]);
    const [dataSourceName, setDataSourceName] = useState("");
    const [loading, setLoading] = useState(true);
    const [addDocumentDialog, setAddDocumentDialog] = useState(false);
    const {downloadDocumentForId} = useApi();
    const navigate = useNavigate();
    const {isAdmin} = useAuth();
    usePageTitle(dataSourceName ? `${DATA_SOURCE_TITLE_PREFIX}${dataSourceName}` : DATA_SOURCE_TITLE_PREFIX, false);
    const {showSnackbar} = useSnackbar();
    const [documentToDelete, setDocumentToDelete] = useState<DocumentInDataSource | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const fetchData = useCallback(async () => {
        setDocuments([]);
        setLoading(true);
        try {
            const [dataSourceResult, docsResult] = await Promise.allSettled([
                axios.get<DataSource>(`/collections/${id}`),
                axios.get(`/documents/by-documents-collection/${id}`, {
                    params: {page: 0, size: 100}
                })
            ]);

            if (dataSourceResult.status === "fulfilled") {
                const dataSourceData = dataSourceResult.value.data;
                setDocumentsDataSourceId(dataSourceData.id.toString());
                setDataSourceName(dataSourceData.name);
            } else {
                console.error("Error fetching data source:", dataSourceResult.reason);
            }

            if (docsResult.status === "fulfilled") {
                setDocuments(docsResult.value.data.content);
            } else {
                console.error("Documents fetch failed:", docsResult.reason);
            }
        } catch (error) {
            console.error("Unexpected error:", error);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const confirmDeleteDocument = async () => {
        if (!documentToDelete) return;

        try {
            await axios.delete(`/documents/${documentToDelete.id}`);
            showSnackbar(t('dataSourceDocuments.deleteSuccess'), "success");
            await fetchData();
        } catch (error) {
            console.error("Error deleting document:", error);
            showSnackbar(t('dataSourceDocuments.deleteError'), "error");
        } finally {
            setIsDeleteDialogOpen(false);
            setDocumentToDelete(null);
        }
    };


    useEffect(() => {
        if (id) {
            void fetchData();
        } else {
            navigate("/data-sources/list");
        }
    }, [id, navigate, fetchData]);

    const [contextMenu, setContextMenu] = useState<{
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        document: DocumentInDataSource;
    } | null>(null);

    const handleContextMenu = useCallback((event: React.MouseEvent, document: DocumentInDataSource) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            anchorEl: event.currentTarget as HTMLElement,
            document,
        });
    }, []);

    const handleCloseContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    const handleMenuPreview = useCallback((doc: DocumentInDataSource) => {
        handleCloseContextMenu();
        navigate(`/documents/view/${doc.id}`);
    }, [handleCloseContextMenu, navigate]);

    const handleMenuDownload = useCallback(async (doc: DocumentInDataSource) => {
        await downloadDocumentForId(doc.id, doc.name);
        handleCloseContextMenu();
    }, [downloadDocumentForId, handleCloseContextMenu]);

    const handleMenuAiAssistant = useCallback((doc: DocumentInDataSource) => {
        handleCloseContextMenu();
        if (doc.conversationId) {
            navigate(`/chat/${doc.conversationId}`);
        }
    }, [handleCloseContextMenu, navigate]);

    const handleMenuDelete = useCallback((doc: DocumentInDataSource) => {
        setDocumentToDelete(doc);
        setIsDeleteDialogOpen(true);
        handleCloseContextMenu();
    }, [handleCloseContextMenu]);

    const columns: ResponsiveTableColumn<DocumentInDataSource>[] = useMemo(() => [
        {
            id: 'name',
            header: t('dataSourceDocuments.name'),
            render: (d) => d.name,
        },
        {
            id: 'owner',
            header: t('dataSourceDocuments.owner'),
            render: (d) => d.owner?.email || t('dataSourceDocuments.noOwner'),
        },
        {
            id: 'size',
            header: t('dataSourceDocuments.size'),
            render: (d) => `${(d.size).toFixed(2)} MB`,
        },
        {
            id: 'extension',
            header: t('dataSourceDocuments.extension'),
            render: (d) => d.extension,
        },
        {
            id: 'createdAt',
            header: t('dataSourceDocuments.createdAt'),
            render: (d) => new Date(d.createdAt).toLocaleDateString(),
        },
        {
            id: 'actions',
            header: '',
            headerLabel: t('common.actions') as string,
            cellClassName: 'actions-cell',
            render: (d) => (
                <>
                    <Tooltip title={t('common.actions')}>
                        <ActionButton
                            className="mobile-only"
                            variant="outlined"
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.document.id === d.id ? `ds-documents-menu-${d.id}` : undefined}
                            aria-expanded={contextMenu?.document.id === d.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, d);
                            }}
                        >
                            {t('common.actions')}
                        </ActionButton>
                    </Tooltip>
                    <Tooltip title={t('common.actions')}>
                        <OptionsButton
                            className="desktop-only"
                            size="small"
                            aria-label={t('common.actions') as string}
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.document.id === d.id ? `ds-documents-menu-${d.id}` : undefined}
                            aria-expanded={contextMenu?.document.id === d.id ? true : undefined}
                            style={{ width: 26 }}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, d);
                            }}
                        >
                            <MoreIcon aria-hidden={true} focusable={false}/>
                        </OptionsButton>
                    </Tooltip>
                </>
            ),
        },
    ], [t, handleContextMenu]);

    return (
        <StyledTableContainer role="region" aria-labelledby="ds-documents-title">
            <Header>
                <div>
                    <BackButton to="/admin/data-sources/list"/>
                </div>
                <div className="title-row">
                    <h2 id="ds-documents-title">{t('dataSourceDocuments.title')}: {dataSourceName || t('dataSourceDocuments.defaultName')}</h2>
                    <Button
                        startIcon={<PlusIcon aria-hidden={true} focusable={false}/>}
                        variant="contained"
                        color="primary"
                        onClick={() => setAddDocumentDialog(true)}
                        aria-label={t('dataSourceDocuments.addDocument') as string}
                    >
                        {t('dataSourceDocuments.addDocument')}
                    </Button>
                </div>
            </Header>
            <div aria-live="polite" aria-atomic={true} aria-busy={loading}>
                {loading && (
                    <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                        {t('dataSourceDocuments.loading')}
                    </div>
                )}
                <ResponsiveTable
                    columns={columns}
                    data={documents}
                    getRowKey={(d) => d.id}
                    emptyState={t('dataSourceDocuments.noDocuments')}
                    ariaLabel={t('dataSourceDocuments.table.ariaLabel') as string}
                />
            </div>

            <ContextMenu
                id={contextMenu ? `ds-documents-menu-${contextMenu.document.id}` : undefined}
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorEl={contextMenu?.anchorEl ?? null}
                anchorReference={contextMenu?.anchorEl ? 'anchorEl' : 'anchorPosition'}
                anchorPosition={
                    contextMenu && contextMenu.mouseY !== undefined && contextMenu.mouseX !== undefined
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
                MenuListProps={{
                    autoFocusItem: true,
                    'aria-labelledby': contextMenu ? `ds-documents-menu-${contextMenu.document.id}` : undefined,
                }}
            >
                {contextMenu && [
                    (
                        <ContextMenuItem key="preview" onClick={() => handleMenuPreview(contextMenu.document)}>
                            <PreviewIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceDocuments.preview')}
                        </ContextMenuItem>
                    ),
                    (
                        <ContextMenuItem key="download" onClick={() => handleMenuDownload(contextMenu.document)}>
                            <DownloadIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceDocuments.download')}
                        </ContextMenuItem>
                    ),
                    (contextMenu.document.conversationId ? (
                        <ContextMenuItem key="assistant" onClick={() => handleMenuAiAssistant(contextMenu.document)}>
                            <ChatIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceDocuments.aiAssistant')}
                        </ContextMenuItem>
                    ) : null),
                    <ContextMenuItem key="delete" onClick={() => handleMenuDelete(contextMenu.document)} style={{color: '#f44336'}}>
                        <DeleteIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('dataSourceDocuments.delete')}
                    </ContextMenuItem>
                ]}
            </ContextMenu>

            {addDocumentDialog && (
                <AddDocumentDialog
                    open={addDocumentDialog}
                    onClose={() => setAddDocumentDialog(false)}
                    dataSourceId={documentsDataSourceId}
                    onDocumentCreated={fetchData}
                />)}
            {documentToDelete && (
                <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={confirmDeleteDocument}
                    title={t('dataSourceDocuments.deleteConfirmTitle')}
                    message={<>{t('dataSourceDocuments.deleteConfirmMessage')} &ldquo;{documentToDelete.name}&rdquo; {t('dataSourceDocuments.deleteConfirmFromSource')}?</>}
                    confirmButtonText={t('dataSourceDocuments.deleteButton') as string}
                    cancelButtonText={t('dataSourceDocuments.cancelButton') as string}
                />
            )}
        </StyledTableContainer>
    );
};

export default DataSourceDocumentList;
